import { ApiClient, PaginatedResponse } from './api';
import type {
  EspacioDisponibleRango,
  DetalleEspacioDisponible,
  CheckoutDisponibilidad,
} from '@/types/availability';

// ===============================
// TIPOS
// ===============================

export interface Espacio {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string;
  tipo_id: number;
  capacidad: number;
  tarifa_base_tipo: number;
  empresa_id: number;
  estado: boolean;
  imagen_principal?: string;
  imagenes?: string[];
  grid_fila?: number;
  grid_columna?: number;
  grid_zona?: string;
  plano_x?: number;
  plano_y?: number;
  plano_ancho?: number;
  plano_alto?: number;
}

export interface BloqueosTemporalesResponse {
  token: string;
  espacio_id: string;
  fecha_entrada: string;
  fecha_salida: string;
  expira_en: string;
  tarifa_total: number;
  cantidad_huespedes: number;
  tiempo_restante_minutos: number;
}

export interface Reserva {
  id: string;
  espacio_id: string;
  huesped_id: string;
  fecha_entrada: string;
  fecha_salida: string;
  cantidad_huespedes: number;
  estado: string;
  estado_pago: string;
  monto_total: number;
  huesped_nombre: string;
  huesped_email: string;
}

export interface IniciarReservaRequest {
  espacio_id: string;
  fecha_entrada: string;
  fecha_salida: string;
  cantidad_huespedes: number;
}

export interface CompletarReservaRequest {
  token: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  numero_documento: string;
  tipo_documento: string;
  solicitudes_especiales?: string;
}

// ===============================
// SERVICIO DE ESPACIOS
// ===============================

export class EspaciosService {
  /**
   * Listar todos los espacios disponibles (público)
   */
  static async listarEspacios(filtros?: any) {
    return ApiClient.getPaginated<Espacio>('/api/espacios', { params: filtros });
  }

  /**
   * Obtener espacio por ID
   */
  static async obtenerEspacio(id: string) {
    return ApiClient.getOne<Espacio>(`/api/espacios/${id}`);
  }

  // ============================================================================
  // ENDPOINTS DE DISPONIBILIDAD (ACTIVOS)
  // ============================================================================

  /**
   * Disponibilidad Simple
   * Obtiene espacios disponibles SIN tarifas (optimizado con disponibilidad_rangos)
   * Uso: SpacesByTypePageV2 - grid de espacios sin precios
   */
  static async disponibilidadSimple(
    fecha_entrada: string,
    fecha_salida: string,
    tipo_espacio_id?: string
  ): Promise<EspacioDisponibleRango[]> {
    const params = new URLSearchParams({
      fecha_entrada,
      fecha_salida,
    });
    if (tipo_espacio_id) {
      params.append('tipo_espacio_id', tipo_espacio_id);
    }

    const response = await ApiClient.get<EspacioDisponibleRango[]>(
      `/api/espacios/disponibilidad_simple?${params.toString()}`
    );
    return Array.isArray(response) ? response : response?.data || [];
  }

  /**
   * Rev Disponibilidad ESPACIO
   * Obtiene disponibilidad detallada de un espacio específico para un rango
   * Uso: DateAvailabilityModal - desglose con tarifas simple y multiple
   */
  static async revDisponibilidadEspacio(
    espacio_id: string,
    fecha_entrada: string,
    fecha_salida: string
  ): Promise<DetalleEspacioDisponible> {
    const params = new URLSearchParams({
      fecha_entrada,
      fecha_salida,
    });

    const response = await ApiClient.get<DetalleEspacioDisponible>(
      `/api/espacios/rev_disponibilidad_espacio/${espacio_id}?${params.toString()}`
    );
    return response as DetalleEspacioDisponible;
  }

  /**
   * Rev Disponibilidad CHECKOUT
   * Validación final de disponibilidad antes de completar reserva
   * Retorna desglose actualizado según cantidad_unidades
   * Uso: CheckoutPage - validación final + recálculo desglose
   */
  static async revDisponibilidadCheckout(
    espacio_id: string,
    fecha_entrada: string,
    fecha_salida: string,
    cantidad_unidades: number,
    bloqueo_temporal_id: string
  ): Promise<CheckoutDisponibilidad> {
    const params = new URLSearchParams({
      fecha_entrada,
      fecha_salida,
      cantidad_unidades: cantidad_unidades.toString(),
      bloqueo_temporal_id,
    });

    const response = await ApiClient.get<CheckoutDisponibilidad>(
      `/api/espacios/rev_disponibilidad_checkout/${espacio_id}?${params.toString()}`
    );
    return response as CheckoutDisponibilidad;
  }

  /**
   * Rev Ocupación Panorámica - TODOS los espacios
   * Endpoint: GET /api/espacios/rev_ocupacion_panoramica
   * Uso: HomePage - mostrar todos los espacios con disponibilidad marcada
   */
  static async revOcupacionPanoramica(
    fecha_entrada: string,
    fecha_salida: string,
    tipo_espacio_id?: string
  ): Promise<EspacioDisponibleRango[]> {
    const params = new URLSearchParams({
      fecha_entrada,
      fecha_salida,
    });
    if (tipo_espacio_id) {
      params.append('tipo_espacio_id', tipo_espacio_id);
    }

    const response = await ApiClient.get<EspacioDisponibleRango[]>(
      `/api/espacios/rev_ocupacion_panoramica?${params.toString()}`
    );
    return Array.isArray(response) ? response : response?.data || [];
  }

  /**
   * Iniciar bloqueo temporal de espacio (5 minutos)
   */
  static async iniciarReserva(data: IniciarReservaRequest) {
    return ApiClient.post<BloqueosTemporalesResponse>('/api/espacios/iniciar-reserva', data);
  }

  /**
   * Completar reserva (después del bloqueo temporal)
   */
  static async completarReserva(data: CompletarReservaRequest) {
    return ApiClient.post<Reserva>('/api/espacios/completar-reserva', data);
  }

  /**
   * Cancelar bloqueo temporal
   */
  static async cancelarBloqueo(token: string) {
    return ApiClient.delete(`/api/espacios/cancelar-bloqueo/${token}`);
  }

  /**
   * Obtener información de reserva
   */
  static async obtenerReserva(id: string) {
    return ApiClient.getOne<Reserva>(`/api/espacios/reserva/${id}`);
  }

  /**
   * ADMIN: Listar espacios (filtrados por empresa activa)
   */
  static async listarEspaciosAdmin(filtros?: any) {
    return ApiClient.getPaginated<Espacio>('/api/admin-protected/espacios', { params: filtros });
  }

  /**
   * ADMIN: Crear espacio
   */
  static async crearEspacio(data: Partial<Espacio>) {
    return ApiClient.post<Espacio>('/api/admin-protected/espacios', data);
  }

  /**
   * ADMIN: Actualizar espacio
   */
  static async actualizarEspacio(id: string, data: Partial<Espacio>) {
    return ApiClient.put<Espacio>(`/api/admin-protected/espacios/${id}`, data);
  }

  /**
   * ADMIN: Eliminar espacio
   */
  static async eliminarEspacio(id: string) {
    return ApiClient.delete(`/api/admin-protected/espacios/${id}`);
  }

  /**
   * ADMIN: Subir imagen de espacio
   */
  static async subirImagen(espacioId: string, archivo: File) {
    return ApiClient.upload<Espacio>(
      `/api/admin-protected/espacios/${espacioId}/imagen`,
      archivo,
      {},
      'imagen_archivo'
    );
  }

  /**
   * Limpiar bloqueos expirados (opcional, endpoint de mantenimiento)
   */
  static async limpiarBloquesExpirados() {
    return ApiClient.post('/api/espacios/limpiar-bloqueos-expirados', {});
  }

  /**
   * Crear reserva como invitado (simplificado)
   * Este endpoint es para crear reserva directamente sin el flujo de bloqueo
   */
  static async crearReservaInvitado(data: any) {
    return ApiClient.post('/api/espacios/reservar-invitado', data);
  }
}

export default EspaciosService;
